import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../utils/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => {
        try { return localStorage.getItem("authToken") || ""; } catch { return ""; }
    });
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem("authUser");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(false);

    // Keep localStorage in sync (guarded)
    useEffect(() => {
        try {
            if (token) localStorage.setItem("authToken", token);
            else localStorage.removeItem("authToken");
        } catch { }
    }, [token]);

    useEffect(() => {
        try {
            if (user) localStorage.setItem("authUser", JSON.stringify(user));
            else localStorage.removeItem("authUser");
        } catch { }
    }, [user]);

    // Optional: hydrate user if token exists but user is empty
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!token) return;
            setLoading(true);
            try {
                const { data } = await api.get("/api/auth/me");
                if (!cancelled) setUser(data?.user || null);
            } catch {
                if (!cancelled) setToken("");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [token]); // <- depend only on token so it runs on every load with a token



    // inside AuthProvider
    const refreshUser = async () => {
        try {
            const { data } = await api.get("/api/auth/me");
            setUser(data?.user || null);
            try { localStorage.setItem("authUser", JSON.stringify(data?.user || null)); } catch { }
        } catch {
            // ignore
        }
    };


    const login = async (email, password) => {
        const { data } = await api.post("/api/auth/login", { email, password });
        setToken(data.token || "");
        setUser(data.user || null);
        return data;
    };

    const register = async (form) => {
        const { data } = await api.post("/api/auth/register", form);
        if (data.token) setToken(data.token);
        if (data.user) setUser(data.user);
        return data;
    };

    const logout = () => {
        setToken("");
        setUser(null);
    };

    const value = useMemo(
        () => ({ token, user, loading, login, register, logout, refreshUser }),
        [token, user, loading]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
